import { useId, useMemo, useRef, useState } from 'react';
import { Search, UserRound, X } from 'lucide-react';
import { matchesQuery } from '@shared/search.ts';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { usePeople, type PersonLite } from '@/lib/usePeople';
import { cn } from '@/lib/utils';

const TRIBE_HE = { kohen: 'כהן', levi: 'לוי', israel: 'ישראל' } as const;

/**
 * Person picker: type the first letters of any name part (any order). Keyboard: ↑ ↓ Enter Esc.
 * `recentIds` float to the top when the query is empty (e.g. recent buyers).
 */
export function PersonAutocomplete({ value, onChange, placeholder = 'חיפוש לפי שם…', recentIds = [], filter, id, disabled, autoFocus }: {
  value: string | null;
  onChange: (personId: string | null, person?: PersonLite) => void;
  placeholder?: string;
  recentIds?: string[];
  filter?: (p: PersonLite) => boolean;
  id?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const { people, loading } = usePeople();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const selected = value ? people.find((p) => p.id === value) ?? null : null;

  const results = useMemo(() => {
    const pool = filter ? people.filter(filter) : people;
    if (!q.trim()) {
      const recent = recentIds.map((rid) => pool.find((p) => p.id === rid)).filter(Boolean) as PersonLite[];
      const rest = pool.filter((p) => !recentIds.includes(p.id));
      return [...recent, ...rest].slice(0, 30);
    }
    return pool.filter((p) => matchesQuery(q, [p.firstName, p.lastName, p.displayName, p.fatherName])).slice(0, 30);
  }, [people, q, recentIds, filter]);

  const choose = (p: PersonLite) => {
    onChange(p.id, p);
    setQ('');
    setOpen(false);
  };

  if (selected && !open) {
    return (
      <div className="flex h-11 w-full items-center gap-2 rounded-xl border border-border bg-surface px-3">
        <UserRound className="size-4 text-muted" />
        <span className="flex-1 truncate">{selected.displayName}</span>
        {!disabled && (
          <button type="button" className="rounded-md p-1 text-muted hover:bg-surface-2" aria-label="ניקוי" onClick={() => { onChange(null); setTimeout(() => inputRef.current?.focus()); }}>
            <X className="size-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <Popover open={open && !disabled} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 start-3 size-4 -translate-y-1/2 text-muted" />
          <input
            ref={inputRef}
            id={id}
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            autoComplete="off"
            autoFocus={autoFocus}
            disabled={disabled}
            value={q}
            placeholder={placeholder}
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQ(e.target.value); setHi(0); setOpen(true); }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
              else if (e.key === 'Enter' && results[hi]) { e.preventDefault(); choose(results[hi]); }
              else if (e.key === 'Escape') setOpen(false);
            }}
            className="h-11 w-full rounded-xl border border-border bg-surface ps-9 pe-3 text-[15px] placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-64 p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => { if (e.target === inputRef.current) e.preventDefault(); }}
      >
        <ul id={listId} role="listbox" className="max-h-72 overflow-y-auto">
          {loading ? (
            <li className="px-3 py-3 text-sm text-muted">טוען…</li>
          ) : results.length === 0 ? (
            <li className="px-3 py-3 text-sm text-muted">{people.length === 0 ? 'עדיין אין אנשי קשר במערכת' : 'לא נמצא אף אחד בשם הזה'}</li>
          ) : results.map((p, i) => (
            <li
              key={p.id}
              role="option"
              aria-selected={i === hi}
              onMouseEnter={() => setHi(i)}
              onMouseDown={(e) => { e.preventDefault(); choose(p); }}
              className={cn('flex min-h-11 cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-1.5', i === hi && 'bg-surface-2')}
            >
              <span className="flex flex-col leading-tight">
                <span>{p.displayName}</span>
                {p.fatherName ? <span className="text-xs text-muted">בן {p.fatherName}</span> : null}
              </span>
              {p.tribe !== 'israel' ? <span className="text-xs text-primary">{TRIBE_HE[p.tribe]}</span> : null}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
